// app/javascript/Pages/DanceStyles/EditDanceStyle.tsx

import React, { useState } from 'react';
import { useForm } from '@mantine/form';
import { usePage } from '@inertiajs/react';
import { TextInput, Button, Box, Text, Group, Title } from '@mantine/core';
import { Dropzone, IMAGE_MIME_TYPE } from '@mantine/dropzone';
import { Notifications, showNotification } from '@mantine/notifications';
import { IconUpload, IconX, IconPhoto, IconCheck, IconAlertCircle } from '@tabler/icons-react';
import ImageCropper from '../../components/ImageCropper';
import FormLayout from '../../components/FormLayout';

interface DanceStyleData {
  id: number;
  name: string;
  slug: string;
  seo_image_url?: string | null;
  cover_image_url?: string | null;
}

interface EditDanceStyleProps extends InertiaPageProps {
  dance_style: DanceStyleData;
  errors?: string[];
  [key: string]: any;
}

export default function EditDanceStyle() {
  const { dance_style, errors } = usePage<EditDanceStyleProps>().props;

  // SEO image states
  const [seoSrc, setSeoSrc] = useState<string | null>(null);
  const [seoBlob, setSeoBlob] = useState<Blob | null>(null);
  const [seoPreview, setSeoPreview] = useState<string | null>(dance_style.seo_image_url || null);

  // Cover image states
  const [coverSrc, setCoverSrc] = useState<string | null>(null);
  const [coverBlob, setCoverBlob] = useState<Blob | null>(null);
  const [coverPreview, setCoverPreview] = useState<string | null>(dance_style.cover_image_url || null);

  const [isLoading, setIsLoading] = useState(false);

  const form = useForm({
    initialValues: {
      name: dance_style.name,
      slug: dance_style.slug,
    },
    validate: {
      name: (v) => (v ? null : 'Name is required'),
      slug: (v) => (v ? null : 'Slug is required'),
    },
  });

  function slugify(str: string) {
    return str
      .toLowerCase()
      .trim()
      .replace(/\s+/g, '-')
      .replace(/[^\w-]+/g, '');
  }

  const handleNameChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const val = e.currentTarget.value;
    form.setFieldValue('name', val);
    form.setFieldValue('slug', slugify(val));
  };

  // Handlers for SEO image
  const onSeoDrop = (files: File[]) => {
    const file = files[0];
    const reader = new FileReader();
    reader.onload = (e) => {
      if (typeof e.target?.result === 'string') setSeoSrc(e.target.result);
    };
    reader.readAsDataURL(file);
  };
  const onSeoCropped = (blob: Blob) => {
    setSeoBlob(blob);
    setSeoPreview(URL.createObjectURL(blob));
    setSeoSrc(null);
  };
  const removeSeo = () => {
    setSeoPreview(null);
    setSeoBlob(null);
  };

  // Handlers for Cover image
  const onCoverDrop = (files: File[]) => {
    const file = files[0];
    const reader = new FileReader();
    reader.onload = (e) => {
      if (typeof e.target?.result === 'string') setCoverSrc(e.target.result);
    };
    reader.readAsDataURL(file);
  };
  const onCoverCropped = (blob: Blob) => {
    setCoverBlob(blob);
    setCoverPreview(URL.createObjectURL(blob));
    setCoverSrc(null);
  };
  const removeCover = () => {
    setCoverPreview(null);
    setCoverBlob(null);
  };

  const handleSubmit = async (values: typeof form.values) => {
    setIsLoading(true);
    const csrf = document.querySelector('meta[name="csrf-token"]')?.getAttribute('content') || '';
    const data = new FormData();

    data.append('dance_style[name]', values.name);
    data.append('dance_style[slug]', values.slug);
    if (seoBlob) data.append('dance_style[seo_image]', seoBlob, 'seo.jpg');
    if (coverBlob) data.append('dance_style[cover_image]', coverBlob, 'cover.jpg');

    try {
      const res = await fetch(`/dance_styles/${dance_style.id}`, {
        method: 'PUT',
        credentials: 'include',
        headers: {
          'X-CSRF-Token': csrf,
          'X-Requested-With': 'XMLHttpRequest',
          Accept: 'application/json',
        },
        body: data,
      });
      const payload = await res.json().catch(() => ({}) as any);

      if (res.ok) {
        showNotification({
          color: 'green',
          title: 'Success!',
          message: 'Dance style updated!',
          icon: <IconCheck />,
          position: 'top-right',
        });
        setTimeout(() => {
          window.location.href = `/dance_styles/${values.slug}`;
        }, 800);
      } else {
        const errs = Array.isArray(payload.errors) ? payload.errors : [];
        if (errs.length) {
          showNotification({
            color: 'red',
            title: 'Error',
            message: errs.join(', '),
            icon: <IconAlertCircle />,
            position: 'top-right',
          });
        } else {
          showNotification({
            color: 'red',
            title: 'Validation Error',
            message: 'Please fix the highlighted fields.',
            icon: <IconAlertCircle />,
            position: 'top-right',
          });
        }
      }
    } catch (err) {
      console.error(err);
      showNotification({
        color: 'red',
        title: 'Error',
        message: 'An unexpected error occurred.',
        icon: <IconAlertCircle />,
        position: 'top-right',
      });
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <FormLayout title="Edit Dance Style">
      <Notifications />

      {/* SEO Image */}
      {!seoPreview && !seoSrc && (
        <Dropzone onDrop={onSeoDrop} maxSize={5 * 1024 ** 2} accept={IMAGE_MIME_TYPE} className="mb-4">
          <Group justify="center" style={{ minHeight: 120, pointerEvents: 'none' }}>
            <Dropzone.Accept>
              <IconUpload size={48} />
            </Dropzone.Accept>
            <Dropzone.Reject>
              <IconX size={48} />
            </Dropzone.Reject>
            <Dropzone.Idle>
              <IconPhoto size={48} />
            </Dropzone.Idle>
            <Text>Drag SEO image here or click</Text>
          </Group>
        </Dropzone>
      )}
      {seoSrc && <ImageCropper src={seoSrc} setCroppedBlob={onSeoCropped} aspectRatio={16 / 9} />}
      {seoPreview && (
        <Box mb="md">
          <img src={seoPreview} alt="SEO preview" style={{ width: '100%' }} />
          <Button variant="filled" color="red" mt="sm" onClick={removeSeo}>
            Remove SEO Image
          </Button>
        </Box>
      )}

      {/* Cover Image */}
      {!coverPreview && !coverSrc && (
        <Dropzone onDrop={onCoverDrop} maxSize={5 * 1024 ** 2} accept={IMAGE_MIME_TYPE} className="mb-4">
          <Group justify="center" style={{ minHeight: 120, pointerEvents: 'none' }}>
            <Dropzone.Accept>
              <IconUpload size={48} />
            </Dropzone.Accept>
            <Dropzone.Reject>
              <IconX size={48} />
            </Dropzone.Reject>
            <Dropzone.Idle>
              <IconPhoto size={48} />
            </Dropzone.Idle>
            <Text>Drag cover image here or click</Text>
          </Group>
        </Dropzone>
      )}
      {coverSrc && <ImageCropper src={coverSrc} setCroppedBlob={onCoverCropped} aspectRatio={16 / 9} />}
      {coverPreview && (
        <Box mb="md">
          <img src={coverPreview} alt="Cover preview" style={{ width: '100%' }} />
          <Button variant="filled" color="red" mt="sm" onClick={removeCover}>
            Remove Cover Image
          </Button>
        </Box>
      )}

      <form onSubmit={form.onSubmit(handleSubmit)}>
        <TextInput
          label="Name"
          placeholder="Dance style name"
          required
          mt="md"
          {...form.getInputProps('name')}
          onChange={handleNameChange}
        />
        <TextInput label="Slug" placeholder="auto-generated" mt="sm" {...form.getInputProps('slug')} />

        {errors && errors.length > 0 && (
          <Box mt="md" style={{ color: 'red' }}>
            {errors.map((e, i) => (
              <div key={i}>{e}</div>
            ))}
          </Box>
        )}

        <Group justify="right" mt="xl">
          <Button type="submit" loading={isLoading}>
            Update Dance Style
          </Button>
        </Group>
      </form>
    </FormLayout>
  );
}
